import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, RefreshControl, Image, TouchableOpacity} from 'react-native';
import {
  Text, Card, Appbar, ActivityIndicator, Snackbar,
  Dialog, Portal, Button, TextInput, IconButton,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import {optionTypeApi} from '../../services/api/optionTypeApi';
import {colors, spacing} from '../../theme';

const MeasurementTypeListScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();

  const [optionTypeId, setOptionTypeId] = useState(null);
  const [measurementTypes, setMeasurementTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  // View modal state
  const [viewOption, setViewOption] = useState(null);
  const [viewVisible, setViewVisible] = useState(false);

  // Edit dialog state
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [displayName, setDisplayName] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const response = await optionTypeApi.listConsolidatedOptionTypes(null, 'MEASUREMENT', true);
      const allOptionTypes = response.data.data;
      const mtType = allOptionTypes.find(ot => ot.code === 'MEASUREMENT_TYPE');
      if (mtType) {
        setOptionTypeId(mtType.id);
        setMeasurementTypes(mtType.options || []);
      }
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [t]);

  useEffect(() => {
    setIsLoading(true);
    fetchData().finally(() => setIsLoading(false));
  }, [fetchData]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  }, [fetchData]);

  const handleToggle = useCallback(async (option, newValue) => {
    if (!optionTypeId) return;
    try {
      const response = await optionTypeApi.toggleOption(optionTypeId, option.id, {isEnabled: newValue});
      setMeasurementTypes(response.data.data.options || []);
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [optionTypeId, t]);

  const openEditDialog = useCallback((option) => {
    setEditingOption(option);
    setDisplayName(option.name);
    setDialogVisible(true);
  }, []);

  const handleSaveOverride = useCallback(async () => {
    if (!editingOption || !optionTypeId) return;
    try {
      const response = await optionTypeApi.updateOptionOverride(optionTypeId, editingOption.id, {
        displayNameOverride: displayName.trim() || null,
      });
      setMeasurementTypes(response.data.data.options || []);
      setDialogVisible(false);
      setEditingOption(null);
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [editingOption, optionTypeId, displayName, t]);

  const sortByEnabled = (a, b) => (b.enabled ? 1 : 0) - (a.enabled ? 1 : 0);
  const bothTypes = measurementTypes.filter(mt => mt.gender === 'BOTH').sort(sortByEnabled);
  const femaleTypes = measurementTypes.filter(mt => mt.gender === 'FEMALE').sort(sortByEnabled);
  const maleTypes = measurementTypes.filter(mt => mt.gender === 'MALE').sort(sortByEnabled);

  const renderOption = (option) => (
    <Card key={option.id} style={[styles.card, !option.enabled && styles.cardDisabled]}>
      <Card.Content style={styles.optionRow}>
        <IconButton
          icon={option.enabled ? 'check-circle' : 'circle-outline'}
          size={24}
          iconColor={option.enabled ? colors.primary : colors.textSecondary}
          onPress={() => handleToggle(option, !option.enabled)}
          style={styles.toggleBtn}
        />
        <TouchableOpacity
          style={styles.cardText}
          activeOpacity={0.7}
          onPress={() => { setViewOption(option); setViewVisible(true); }}>
          <Text variant="titleMedium" style={[styles.typeName, !option.enabled && styles.textDisabled]}>
            {option.name}
          </Text>
          <Text variant="bodySmall" style={styles.typeCode}>{option.code}</Text>
        </TouchableOpacity>
        <IconButton
          icon="pencil-outline"
          size={20}
          iconColor={colors.textSecondary}
          onPress={() => openEditDialog(option)}
          style={styles.actionBtn}
        />
      </Card.Content>
    </Card>
  );

  const renderSection = (title, types) => {
    if (types.length === 0) return null;
    return (
      <>
        <Text variant="titleSmall" style={styles.sectionHeader}>{title}</Text>
        {types.map(renderOption)}
      </>
    );
  };

  if (isLoading && measurementTypes.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content title={t('measurement.measurementTypes')} titleStyle={styles.appbarTitle} />
      </Appbar.Header>

      <ScrollView
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />}>
        <View style={styles.listContent}>
          {renderSection(t('measurement.both'), bothTypes)}
          {renderSection(t('customer.female'), femaleTypes)}
          {renderSection(t('customer.male'), maleTypes)}
        </View>
        <View style={{height: 80}} />
      </ScrollView>

      {/* View Measurement Type Modal */}
      <Portal>
        <Dialog
          visible={viewVisible}
          onDismiss={() => setViewVisible(false)}
          style={{borderRadius: 15}}>
          <Dialog.Title>{viewOption?.name}</Dialog.Title>
          <Dialog.Content>
            <Text variant="bodyMedium" style={styles.viewCode}>{viewOption?.code}</Text>
            <View style={styles.imageRow}>
              {viewOption?.imageUrl1 ? (
                <Image source={{uri: viewOption.imageUrl1}} style={styles.viewImage} />
              ) : (
                <View style={styles.emptyImage} />
              )}
              {viewOption?.imageUrl2 ? (
                <Image source={{uri: viewOption.imageUrl2}} style={styles.viewImage} />
              ) : (
                <View style={styles.emptyImage} />
              )}
            </View>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setViewVisible(false)}>
              {t('common.ok')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      {/* Edit Measurement Type Override Dialog */}
      <Portal>
        <Dialog
          visible={dialogVisible}
          onDismiss={() => setDialogVisible(false)}
          style={{borderRadius: 15}}>
          <Dialog.Title>{t('optionType.editOption')}</Dialog.Title>
          <Dialog.Content>
            <TextInput
              label={t('optionType.displayName')}
              value={displayName}
              onChangeText={setDisplayName}
              mode="outlined"
              style={styles.dialogInput}
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDialogVisible(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              mode="contained"
              onPress={handleSaveOverride}
              style={styles.saveButton}>
              {t('common.save')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  listContent: {padding: spacing.sm},
  sectionHeader: {
    fontWeight: '600', color: colors.textSecondary,
    marginTop: spacing.md, marginBottom: spacing.xs, marginLeft: spacing.sm,
  },
  card: {
    marginHorizontal: spacing.xs,
    marginTop: spacing.xs,
    backgroundColor: colors.surface,
    borderRadius: 8,
  },
  cardDisabled: {
    opacity: 0.5,
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.xs,
  },
  cardText: {flex: 1},
  typeName: {fontWeight: '600', color: colors.text},
  typeCode: {color: colors.textSecondary, marginTop: 2},
  textDisabled: {color: colors.textSecondary},
  toggleBtn: {margin: 0},
  actionBtn: {margin: 0},
  dialogInput: {
    marginBottom: spacing.sm,
    backgroundColor: colors.surface,
  },
  saveButton: {
    backgroundColor: colors.primary,
    borderRadius: 10,
    paddingHorizontal: 10,
  },
  viewCode: {
    color: colors.textSecondary,
    marginBottom: spacing.md,
  },
  imageRow: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  viewImage: {
    width: 75,
    height: 75,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#CCC',
    backgroundColor: colors.background,
  },
  emptyImage: {
    width: 75,
    height: 75,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#CCC',
    backgroundColor: colors.background,
  },
});

export default MeasurementTypeListScreen;
